import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import RadioButtonChecked from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import SearchIcon from "@mui/icons-material/Search";
import {
  Chip,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { ProgramIcon } from "../../programs/components/program-icon";

import type { ProgramNode } from "../../program-tree/program-tree";
import type { MoveDestination } from "../move-destinations";
import type { FC } from "react";

/**
 * A search, and the programs and folders from moveDestinations as a radio group. Each level is
 * indented. A search result shows the programs above it. Put it in a Stack to space the parts.
 */
export const DestinationList: FC<{
  query: string;
  onQuery: (query: string) => void;
  rows: MoveDestination[];
  /** The id of the chosen program or folder. */
  chosen: ProgramNode["id"] | undefined;
  onPick: (node: ProgramNode) => void;
  /** The data-analytics-id of each choice. */
  analyticsId: string;
}> = ({ query, onQuery, rows, chosen, onPick, analyticsId }) => {
  const searching = query.trim() !== "";

  return (
    <>
      <TextField
        autoFocus
        fullWidth
        size="small"
        type="search"
        label="Find a program or folder"
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      <List role="radiogroup" aria-label="Destination" sx={{ py: 0 }}>
        {rows.map(({ node, depth, path, current, disabled }) => {
          const checked = chosen === node.id;
          return (
            <ListItemButton
              key={node.id}
              role="radio"
              aria-checked={checked}
              selected={checked}
              disabled={disabled}
              onClick={() => onPick(node)}
              sx={{ pl: searching ? 1.5 : 1.5 + depth * 3, borderRadius: "6px" }}
              data-analytics-id={analyticsId}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {depth ? (
                  <FolderOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                ) : (
                  <ProgramIcon
                    data={node.icon_value}
                    fontSize="small"
                    sx={{ color: node.theme_colour }}
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary={node.title}
                secondary={searching && path ? path : undefined}
                slotProps={{
                  primary: { noWrap: true, sx: { fontWeight: depth ? undefined : 600 } },
                  secondary: { noWrap: true },
                }}
              />
              {current ? (
                <Chip size="small" label="Current location" sx={{ ml: 1, fontSize: 11 }} />
              ) : null}
              {checked ? (
                <RadioButtonChecked fontSize="small" color="primary" sx={{ ml: 1.5 }} />
              ) : (
                <RadioButtonUnchecked fontSize="small" sx={{ ml: 1.5, color: "neutral.400" }} />
              )}
            </ListItemButton>
          );
        })}
      </List>
      {rows.length ? null : (
        <Typography variant="body2" sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
          No programs or folders match that search.
        </Typography>
      )}
    </>
  );
};
